import PropTypes from 'prop-types';
import React__default, { useRef, useCallback, useMemo } from 'react';
import { useFormFieldProps } from '../form/form.context.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useId } from '../../hooks/use-id.js';
import { SwanListboxContextProvider } from './listbox.context.js';
import { filterProps } from '../core/core.utils.js';
import { CollectionContextProvider } from '../../contexts/internal/collection/collection.component.js';
import { deprecatedPropValues } from '../core/deprecated-prop-values.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * The size (height) of the Listbox.
   * One of: "standard", "mini".
   */ size: PropTypes.oneOf([
        'standard',
        'mini'
    ]),
    /**
   * The visual style of the Listbox.
   * One of: "standard", "link", "error".
   *
   * 'link' value is deprecated - use the default styling instead
   */ skin: deprecatedPropValues(PropTypes.oneOf([
        'standard',
        'link',
        'error'
    ]), [
        'link'
    ], 'Omit or use the `standard` skin instead'),
    /**
   * Whether or not the Listbox should expand to fill the entire width of its container.
   */ fullWidth: PropTypes.bool,
    /**
   * Whether or not the Listbox is disabled.
   */ disabled: PropTypes.bool
};
const propKeysToRemove = [
    ...Object.keys(propTypes),
    'selectionMode',
    'selectedKeys',
    'disabledKeys',
    'onSelectionChange',
    'onChange',
    'value',
    'items'
];
/*
 * A utility hook to convert all the more user friendly props into the context value
 */ const useSelectionProps = ({ selectionMode = 'single', selectedKeys, onSelectionChange, value, onChange })=>{
    // warn (only once) if using the legacy props with multiple selection mode
    const warned = useRef(false);
    if (selectionMode !== 'single' && (onChange || value) && !warned.current) {
        console.warn('Listbox - Do not use "onChange" or "value" props with multiple selection mode. Only one item will be selected');
        warned.current = true;
    }
    const onSelectedChanged = useCallback((keys)=>{
        if (onSelectionChange) onSelectionChange(keys);
        if (onChange && selectionMode === 'single' && keys !== 'all') {
            onChange([
                ...keys
            ][0]);
        }
    }, [
        onChange,
        onSelectionChange,
        selectionMode
    ]);
    return useMemo(()=>({
            selectionMode: selectionMode,
            selectedKeys: value ? new Set([
                value
            ]) : selectedKeys,
            onSelectionChange: onSelectedChanged
        }), [
        selectionMode,
        value,
        selectedKeys,
        onSelectedChanged
    ]);
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Listbox = renderWithRef('Listbox', propTypes, (props, ref)=>{
    useComponentStylesLoaded('Listbox', [
        SWAN_STYLE_KEY_MAP.listbox,
        SWAN_STYLE_KEY_MAP.popover
    ]);
    const { children, className, disabled, fullWidth, size, skin } = props;
    const selectionProps = useSelectionProps(props);
    const formProps = useFormFieldProps(useId(props.id));
    const classes = new Set([
        'swan-listbox'
    ]);
    if (size === 'mini') classes.add('swan-listbox-mini');
    if (skin === 'error' || (formProps === null || formProps === void 0 ? void 0 : formProps['aria-invalid'])) classes.add('swan-listbox-skin-error');
    if (skin === 'link') classes.add('swan-listbox-skin-link');
    if (fullWidth) classes.add('swan-listbox-full-width');
    if (className) classes.add(className);
    const filteredProps = filterProps(props, propKeysToRemove, processStyleProps(props, classes));
    return /*#__PURE__*/ React__default.createElement("div", _object_spread_props(_object_spread({}, filteredProps, formProps), {
        ref: ref
    }), /*#__PURE__*/ React__default.createElement(CollectionContextProvider, {
        items: props.items,
        disabledKeys: props.disabledKeys
    }, /*#__PURE__*/ React__default.createElement(SwanListboxContextProvider, {
        fullWidth: fullWidth,
        disabled: disabled !== null && disabled !== void 0 ? disabled : false,
        selectionProps: selectionProps
    }, children)));
});

export { Listbox };
